import { fetchConfig } from '../config';

// Detect if running on localhost
const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

// WebSocket URL - fetch from config in production
let WS_URL = null;
let configPromise = null;

const getWsUrl = async () => {
  if (isLocalhost) {
    return 'ws://localhost:8000/chat/ws';
  }
  
  if (!configPromise) {
    configPromise = fetchConfig().then(config => {
      // Use WebSocket URL from config
      WS_URL = config.websocket?.url || 'wss://api.smartgtd.space/chat/ws';
      return WS_URL;
    }).catch(err => {
      console.error('Failed to fetch WebSocket config:', err);
      // Fallback to default
      return 'wss://api.smartgtd.space/chat/ws';
    });
  }
  return configPromise;
};

class WebSocketClient {
  constructor() {
    this.ws = null;
    this.messageHandlers = [];
    this.connectionHandlers = [];
    this.connected = false;
    this.token = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 2000;
    this.shouldReconnect = true;
  }

  async connect(token) {
    // Get fresh token from Cognito SDK storage if available
    if (!token) {
      const config = await fetchConfig();
      const keyPrefix = `CognitoIdentityServiceProvider.${config.cognito.clientId}`;
      const lastAuthUser = localStorage.getItem(`${keyPrefix}.LastAuthUser`);
      if (lastAuthUser) {
        token = localStorage.getItem(`${keyPrefix}.${lastAuthUser}.idToken`);
      }
    }
    
    if (!token) {
      throw new Error('No authentication token available');
    }
    
    this.token = token;
    this.shouldReconnect = true;
    const wsUrl = await getWsUrl();
    
    return new Promise((resolve, reject) => {
      this.ws = new WebSocket(`${wsUrl}?token=${token}`);
      
      this.ws.onopen = () => {
        console.log('WebSocket connected');
        this.connected = true;
        this.reconnectAttempts = 0;
        this.notifyConnectionChange(true);
        resolve();
      };
      
      this.ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        this.connected = false;
        this.notifyConnectionChange(false);
        reject(error);
      };
      
      this.ws.onmessage = (event) => {
        console.log('WebSocket message:', event.data);
        const data = JSON.parse(event.data);
        
        // Check for subscription or usage limit errors
        if (data.type === 'error' && (data.message === 'SUBSCRIPTION_REQUIRED' || data.message === 'USAGE_LIMIT_EXCEEDED')) {
          this.shouldReconnect = false; // Stop reconnection attempts
        }
        
        this.messageHandlers.forEach(handler => handler(data));
      };
      
      this.ws.onclose = (event) => {
        console.log('WebSocket closed', event.code, event.reason);
        this.connected = false;
        this.notifyConnectionChange(false);
        
        // Don't reconnect on subscription error (code 4002) or usage limit (code 4003)
        if (event.code === 4002 || event.code === 4003) {
          this.shouldReconnect = false;
          console.log('Subscription or usage limit error - stopping reconnection');
          return;
        }
        
        // Auto-reconnect if enabled and not manually disconnected
        if (this.shouldReconnect && this.reconnectAttempts < this.maxReconnectAttempts) {
          this.reconnectAttempts++;
          console.log(`Reconnecting... attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts}`);
          setTimeout(() => {
            if (this.shouldReconnect) {
              this.connect(this.token).catch(err => {
                console.error('Reconnection failed:', err);
              });
            }
          }, this.reconnectDelay * this.reconnectAttempts);
        }
      };
    });
  }

  sendMessage(message, options = {}) {
    const payload = { 
      message, 
      token: this.token,
      agent: options.agent || 'orchestrator'
    };
    console.log('WebSocket state:', this.ws?.readyState, 'Connected:', this.connected);
    console.log('Sending payload:', JSON.stringify(payload));
    
    if (this.connected && this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(payload));
      console.log('Message sent successfully');
    } else {
      console.error('WebSocket not connected. Connected:', this.connected, 'State:', this.ws?.readyState);
    }
  }

  onMessage(handler) {
    this.messageHandlers.push(handler);
  }

  onConnectionChange(handler) {
    this.connectionHandlers.push(handler);
  }

  notifyConnectionChange(connected) {
    this.connectionHandlers.forEach(handler => handler(connected));
  }

  disconnect() {
    this.shouldReconnect = false;
    this.connected = false;
    this.ws?.close();
    this.messageHandlers = [];
    this.connectionHandlers = [];
  }
}

export default new WebSocketClient();

